/* eslint-disable react/no-did-update-set-state */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import {
  isEmpty,
  isEqual,
  pick,
  intersectionWith,
  isFunction,
  isArray,
  every,
  get
} from 'lodash';
import SegmentsList from './SegmentsList';
import SegmentForm from './SegmentForm';
import { formatFilters } from '../../utils/segments';

const convertToArray = value => {
  if (!value) {
    return [];
  }
  return isArray(value) ? value : [value];
};

const areFiltersValuesValid = (availableFilterValue = [], filterValue = []) => {
  const intersectionItems = intersectionWith(
    availableFilterValue,
    filterValue,
    (arrVal, othVal) => '' + arrVal.value === '' + othVal.value
  );
  return intersectionItems.length !== 0;
};

const areRequiredFiltersValuesValid = (requiredValues, segmentFilters) => {
  let isValid = true;
  const isMovieShowValue =
    Array.isArray(requiredValues) &&
    (requiredValues.includes('movie') || requiredValues.includes('show'));
  if (requiredValues && !isMovieShowValue) {
    if (isFunction(requiredValues)) {
      isValid = requiredValues(segmentFilters);
    } else if (requiredValues.length) {
      isValid = every(requiredValues, requiredValue => {
        const segmentFilter = segmentFilters.find(
          segmentFilter => segmentFilter.id === requiredValue
        );
        const filterValue = get(segmentFilter, 'value.length');
        return filterValue && filterValue > 0;
      });
    }
  }
  return isValid;
};

const createSegmentFilterValidator = (
  availableFilters = {},
  titleFields = []
) => ({ id, value: filterValue }) => {
  const isTitleField = titleFields.includes(id);
  let availableFilter = availableFilters[id];
  let isAvailableFilter = !!availableFilter;
  let isValid = isAvailableFilter;
  if (isTitleField) {
    if (isAvailableFilter) {
      const availableFilterValue = convertToArray(availableFilter.value);
      if (isValid && availableFilterValue.length) {
        isValid = areFiltersValuesValid(availableFilterValue, filterValue);
      }
    } else {
      // is there is no 'show' or 'movie' filter check for 'title' filter
      isValid = !!availableFilters['title'];
    }
  }
  return isValid;
};

class AudienceSegment extends React.Component {
  static getDerivedStateFromProps(props, state) {
    if (!isEqual(props.config, state.config)) {
      return {
        config: props.config,
        filters: formatFilters(props.config)
      };
    }
    return null;
  }

  state = {
    name: this.props.name,
    id: this.props.id,
    filters: [],
    config: null,
    showSegments: !this.props.filters || this.props.filters.length < 1,
    editing: false
  };

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.filters, this.props.filters) &&
      this.props.filters &&
      this.props.filters.length > 0
    ) {
      const config = formatFilters(this.props.config);
      this.props.filters.forEach(f => {
        if (f.value) {
          const c = config.filter(cf => cf.id === f.id)[0];
          if (c) {
            c.value = f.value;
          }
        }
      });
      this.setState({
        showSegments: false,
        filters: config
      });
    }
  }

  handleEdit = (id, name, currentFilters) => {
    const selectedFilters = JSON.parse(currentFilters);
    const activeFilters = this.state.filters.map(filter => {
      const af = selectedFilters.find(f => f.id === filter.id);
      if (af) {
        const { value } = af;
        return { ...filter, value };
      }
      return filter;
    });
    this.setState({
      showSegments: false,
      editing: true,
      name,
      id,
      filters: activeFilters
    });
  };

  backtoSegmentList = () => {
    this.setState({
      name: this.props.name,
      id: this.props.id,
      filters: [],
      config: null,
      showSegments: true,
      editing: false
    });
  };

  handleApply = async segment => {
    const { onClose, onChangeSegment, handleApply } = this.props;

    await onClose();

    onChangeSegment('segments', [segment]);

    this.setState(
      {
        showSegments: true
      },
      handleApply(segment)
    );
  };

  handleSave = async (id, n, filts) => {
    const { onClose, onSave, onChangeSegment } = this.props;
    const activeFilters = filts
      .filter(({ value }) => !isEmpty(value))
      .map(f =>
        pick(f, [
          'value',
          'id',
          'isAsync',
          'isMulti',
          'label',
          'title',
          'value'
        ])
      );

    const response = await onSave(id, n, JSON.stringify(activeFilters));

    if (!response) {
      // Show error message
      return null;
    }

    if (this.state.editing) {
      return this.backtoSegmentList();
    }

    await onClose();

    onChangeSegment('segments', [
      {
        filters: activeFilters,
        label: n,
        name: n,
        value: activeFilters
      }
    ]);

    this.setState({
      name: '',
      filters: [],
      config: null,
      showSegments: true
    });
  };

  handleClose = () => {
    this.toggleCreateSegmentModal();
    this.setState({
      name: this.props.name,
      id: this.props.id,
      filters: [],
      config: null,
      showSegments: true
    });
    this.props.onClose();
  };

  toggleCreateSegmentModal = () => {
    this.setState({ showSegments: !this.state.showSegments });
  };

  applicableSegments = () => {
    const {
      segments,
      availableFilters,
      requiredValues,
      titleFields
    } = this.props;
    const isFilterValid = createSegmentFilterValidator(
      availableFilters,
      titleFields
    );
    return segments.map(segment => {
      const segmentFilters = JSON.parse(segment.filters);
      let isInvalid = !areRequiredFiltersValuesValid(
        requiredValues,
        segmentFilters
      );
      segmentFilters.forEach(segmentFilter => {
        isInvalid = isInvalid || !isFilterValid(segmentFilter);
      });
      return { ...segment, ...{ invalid: isInvalid } };
    });
  };

  render() {
    const { isOpen, onClose } = this.props;

    return (
      <Modal
        className="tvt-audience-segment"
        show={isOpen}
        onHide={this.handleClose}
        backdrop
      >
        {this.state.showSegments ? (
          <SegmentsList
            openCreate={this.toggleCreateSegmentModal}
            onClose={onClose}
            handleApply={this.handleApply}
            handleEdit={this.handleEdit}
            segments={this.applicableSegments()}
          />
        ) : (
          <SegmentForm
            {...this.props}
            filters={this.state.filters}
            name={this.state.name}
            id={this.state.id}
            handleSave={this.handleSave}
            handleClose={this.handleClose}
            backtoSegmentList={this.backtoSegmentList}
          />
        )}
      </Modal>
    );
  }
}

AudienceSegment.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  filters: PropTypes.string,
  config: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChangeSegment: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  onUpdateFilterOptions: PropTypes.func.isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({})),
  availableFilters: PropTypes.shape([]).isRequired,
  requiredValues: PropTypes.arrayOf(PropTypes.string),
  titleFields: PropTypes.arrayOf(PropTypes.string)
};

AudienceSegment.defaultProps = {
  id: '',
  name: '',
  filters: [],
  segments: [],
  requiredValues: [],
  titleFields: []
};

export default AudienceSegment;
